import { engineComponent } from "neo-common";
import { BaseElement } from "../Graphics/BaseElement";
import { ComponentTypes } from "../../ComponentTypes";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../../Utils/FrameTimer";

@engineComponent(ComponentTypes.DisplayObject)
export class ImageTextElement extends BaseElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public textAlign: string;

    public textBaseline: string;

    public text: string;

    public font: string;

    protected fontScale: number = 1;

    public bitmapText: PIXI.extras.BitmapText;

    protected textTimer: FrameTimerEventEmitter;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.textTimer = CreateFrameTimer("timer");

        this.textAlign = ImageTextElementAttributes.TEXT_ALIGN_VALUE.LEFT;
        this.textBaseline = ImageTextElementAttributes.TEXT_BASELINE_VALUE.TOP;
        this.text = "";
    }

    //******************************************************************************************************************
    //  CREATE, UPDATE
    //******************************************************************************************************************

    public createByNode(node: Element) {
        if (node.hasAttribute(ImageTextElementAttributes.FONT_SCALE)) {
            this.fontScale = parseFloat(node.getAttribute(ImageTextElementAttributes.FONT_SCALE));
            if (this.bitmapText) {
                this.bitmapText.scale.x = this.fontScale;
                this.bitmapText.scale.y = this.fontScale;
            }
        }

        super.createByNode(node);

        if (this.bitmapText == null) {
            if (node.hasAttribute(ImageTextElementAttributes.FONT)) {
                if (node.hasAttribute(ImageTextElementAttributes.TEXT)) {
                    this.text = node.getAttribute(ImageTextElementAttributes.TEXT);
                }

                this.font = node.getAttribute(ImageTextElementAttributes.FONT);

                this.bitmapText = new PIXI.extras.BitmapText(this.text, { font: this.font });
                this.bitmapText.scale.x = this.fontScale;
                this.bitmapText.scale.y = this.fontScale;

                this.addChild(this.bitmapText);
            }
        }
    }

    public updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(ImageTextElementAttributes.TEXT)) {
            if (node.hasAttribute(ImageTextElementAttributes.DELAY)) {
                this.textTimer.once(
                    FrameTimerEvents.Complete,
                    this.setText.bind(this, node.getAttribute(ImageTextElementAttributes.TEXT)),
                );
                this.textTimer.start(parseInt(node.getAttribute(ImageTextElementAttributes.DELAY)));
            } else {
                this.setText(node.getAttribute(ImageTextElementAttributes.TEXT));
            }
        }

        if (node.hasAttribute(ImageTextElementAttributes.TEXT_ALIGN)) {
            this.textAlign = node.getAttribute(ImageTextElementAttributes.TEXT_ALIGN);

            this.realign();
        }

        if (node.hasAttribute(ImageTextElementAttributes.TEXT_BASELINE)) {
            this.textBaseline = node.getAttribute(ImageTextElementAttributes.TEXT_BASELINE);

            this.realign();
        }

        if (node.hasAttribute(ImageTextElementAttributes.TINT)) {
            let val = parseInt(node.getAttribute(ImageTextElementAttributes.TINT), 16);
            if (!isNaN(val)) {
                this.bitmapText.tint = val;
            }
        }

        if (node.hasAttribute(ImageTextElementAttributes.FONT_SCALE)) {
            this.fontScale = parseFloat(node.getAttribute(ImageTextElementAttributes.FONT_SCALE));
            this.bitmapText.scale.x = this.fontScale;
            this.bitmapText.scale.y = this.fontScale;
        }
    }

    //******************************************************************************************************************
    //  REALIGN
    //******************************************************************************************************************

    public realign(): void {
        switch (this.textAlign) {
            case ImageTextElementAttributes.TEXT_ALIGN_VALUE.RIGHT:
                this.bitmapText.position.x = -this.bitmapText.width;
                break;
            case ImageTextElementAttributes.TEXT_ALIGN_VALUE.CENTER:
                this.bitmapText.position.x = -this.bitmapText.width * 0.5;
                break;
            default:
                this.bitmapText.position.x = 0;
                break;
        }

        switch (this.textBaseline) {
            case ImageTextElementAttributes.TEXT_BASELINE_VALUE.BOTTOM:
                this.bitmapText.position.y = -this.bitmapText.height;
                break;
            case ImageTextElementAttributes.TEXT_BASELINE_VALUE.MIDDLE:
                this.bitmapText.position.y = -this.bitmapText.height * 0.5;
                break;
            default:
                this.bitmapText.position.y = 0;
                break;
        }

        this.bitmapText.scale.x = this.fontScale;
        this.bitmapText.scale.y = this.fontScale;
    }

    public setText(value: string) {
        this.removeChild(this.bitmapText);
        this.text = value;
        this.bitmapText = new PIXI.extras.BitmapText(this.text, { font: this.font });
        this.addChild(this.bitmapText);
        this.realign();
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    public clone(): ImageTextElement {
        let element: ImageTextElement = new ImageTextElement();
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;

        element.font = this.font;
        element.fontScale = this.fontScale;
        element.text = this.text;
        element.textAlign = this.textAlign;
        element.textBaseline = this.textBaseline;

        element.bitmapText = new PIXI.extras.BitmapText(this.text, { font: this.font });
        element.addChild(element.bitmapText);

        element.realign();
        element.markAsCloned();

        return element;
    }
}

export const ImageTextElementAttributes = {
    /**
     * The text to display.
     * @property Text
     * @type string
     * @default null
     */
    TEXT: "Text",
    DELAY: "Delay",
    /**
     * Bitmap font for text.
     * @property Font
     * @type string
     * @default null
     */
    FONT: "Font",
    /**
     * Scale for font.
     * @property FontScale
     * @type number
     * @default 1
     */
    FONT_SCALE: "FontScale",
    /**
     * The horizontal text alignment. Any of "left", "right", and "center".
     * For detailed information view the whatwg spec. Default is "left".
     * @property TextAlign
     * @type string
     * @default left
     */
    TEXT_ALIGN: "TextAlign",
    TEXT_ALIGN_VALUE: {
        LEFT: "left",
        CENTER: "center",
        RIGHT: "right",
    },
    /**
     * The vertical alignment point on the text. Any of "top", "middle" or "bottom". Default is "top".
     * @property TextBaseline
     * @type string
     * @default top
     */
    TEXT_BASELINE: "TextBaseline",
    TEXT_BASELINE_VALUE: {
        TOP: "top",
        MIDDLE: "middle",
        BOTTOM: "bottom",
    },
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Texts/ImageTextElement.ts
 **/